import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Monitor, Smartphone, History } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDateTime } from "@/lib/datetime";
import { errMsg } from "@/components/email/EmParts";
import EmailBuilder, { exportDesign, showPreview } from "@/components/email/EmailBuilder";
import BuilderSidebar from "@/components/email/BuilderSidebar";

function Versions({ tid, onRestore }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/email-marketing/templates/${tid}/versions`).then((r) => setRows(r.data)).catch(() => {}); }, [tid]);
  const restore = async (v) => { const { data } = await api.get(`/email-marketing/templates/${tid}/versions/${v.id}`); onRestore(data); };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-1" data-testid="em-tpl-versions">
      <p className="text-xs font-semibold text-slate-700">Version history</p>
      {rows.map((v) => <button type="button" key={v.id} onClick={() => restore(v)} className="w-full text-left text-xs hover:bg-slate-50 rounded px-1 py-0.5" data-testid={`em-tpl-version-${v.id}`}>v{v.version} · {formatDateTime(v.saved_at)} · {v.saved_by}</button>)}
      {!rows.length && <p className="text-xs text-slate-400">No earlier versions yet.</p>}
    </div>
  );
}

export default function EmTemplateEditor() {
  const { id } = useParams();
  const { can } = useAuth();
  const manage = can("email_marketing.manage");
  const editor = useRef(null);
  const [t, setT] = useState(null);
  const [brand, setBrand] = useState({});
  const [hist, setHist] = useState(false);
  const [draftAt, setDraftAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/email-marketing/templates/${id}`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); api.get("/email-marketing/brand").then((r) => setBrand(r.data)); }, [load]);
  if (!t) return null;
  const autosave = (design, html) => { if (manage) api.post(`/email-marketing/templates/${id}/autosave`, { design, html }).then((r) => setDraftAt(r.data.draft_saved_at)).catch(() => {}); };
  const save = async () => {
    setBusy(true);
    try { const d = await exportDesign(editor.current); const { data } = await api.put(`/email-marketing/templates/${id}`, { name: t.name, category: t.category, design: d.design, html: d.html }); setT({ ...data }); setDraftAt(null); toast.success(`Saved as version ${data.version}`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const recover = () => { editor.current.loadDesign(t.draft_design); setT({ ...t, has_newer_draft: false }); toast.success("Unsaved draft restored. Save to keep it."); };
  const discard = async () => { await api.delete(`/email-marketing/templates/${id}/draft`); setT({ ...t, has_newer_draft: false }); };
  return (
    <div className="space-y-3" data-testid="em-template-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/email-marketing/templates" className="inline-flex items-center gap-1 text-sm text-slate-500" data-testid="em-tpl-back"><ArrowLeft className="w-4 h-4" />Templates</Link>
        <Input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} className="w-64 h-9" disabled={!manage} data-testid="em-tpl-name" />
        <span className="text-xs text-slate-400" data-testid="em-tpl-version">v{t.version}{draftAt ? ` · draft autosaved ${formatDateTime(draftAt)}` : ""}</span>
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="outline" onClick={() => showPreview(editor.current, "desktop")} data-testid="em-tpl-preview-desktop"><Monitor className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => showPreview(editor.current, "mobile")} data-testid="em-tpl-preview-mobile"><Smartphone className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setHist(!hist)} data-testid="em-tpl-history-btn"><History className="w-4 h-4" /></Button>
          {manage && <Button size="sm" className="gap-1" onClick={save} disabled={busy} data-testid="em-tpl-save"><Save className="w-4 h-4" />Save</Button>}
        </div>
      </div>
      {t.has_newer_draft && <div className="flex flex-wrap items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="em-tpl-draft-banner">
        Unsaved changes from {formatDateTime(t.draft_saved_at)} were found.<Button size="sm" variant="outline" onClick={recover} data-testid="em-tpl-draft-restore">Restore draft</Button><Button size="sm" variant="ghost" onClick={discard} data-testid="em-tpl-draft-discard">Discard</Button></div>}
      <div className="grid xl:grid-cols-[1fr_280px] gap-3">
        <EmailBuilder design={t.design} editorRef={editor} onChange={autosave} />
        <div className="space-y-3">{hist && <Versions tid={id} onRestore={(v) => { editor.current.loadDesign(v.design); toast.success(`Loaded version ${v.version}. Save to make it current.`); }} />}<BuilderSidebar brand={brand} /></div>
      </div>
    </div>
  );
}
